import { createContext, useContext, useState, useEffect } from 'react';
import { useAlertContext } from './AlertContext';
import { loginUser, signupUser, getMe } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { showAlert } = useAlertContext();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (token) {
        try {
            // Verify token and get fresh user data
            const response = await getMe();
            const userData = response.user;
            
            setUser(userData);
            // Update local storage to keep it somewhat fresh
            localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
            console.error('Auth check failed:', error);
            // If token is invalid, clear everything
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            setUser(null);
        }
      } else {
        console.log('AuthContext useEffect - no token found');
      }
      setLoading(false);
    };

    checkUser();
  }, []);

  const login = async (email, password, remember = true) => {
    try {
      const response = await loginUser(email, password);
      const { token, user } = response;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true };
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      showAlert({
        type: 'error',
        title: 'Login Failed',
        message: errorMessage
      });
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'An error occurred',
      };
    }
  };

  const signup = async (username, email, password) => {
    try {
      const response = await signupUser({ username, email, password });
      const { token, user } = response;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true };
    } catch (error) {
      console.error('Signup error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'An error occurred',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, signup, logout, loading, rememberMe, setRememberMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 